import LoginDeviceCheck from "@/components/LoginDeviceCheck";
import "@/styles/auth.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Fingerprint, Eye, EyeOff, Loader2, AlertTriangle, Mail, LockKeyhole, ArrowRight, UsersRound, BriefcaseBusiness, Truck, ChartNoAxesColumnIncreasing,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { canOfferBiometricUnlock, unlockWithPasskey } from "@/lib/passkeys";

const FEATURES = [
  { Icon: UsersRound, title: "People", description: "Teams, timesheets and payroll" },
  { Icon: BriefcaseBusiness, title: "Jobs", description: "From quotes to completion" },
  { Icon: Truck, title: "Plant & Fleet", description: "Keep your machines moving" },
  { Icon: ChartNoAxesColumnIncreasing, title: "Operations", description: "Landfill, materials, compliance and more" },
];

function Contours({ variant }) {
  return <svg className={`auth-contours auth-contours--${variant}`} viewBox="0 0 600 600" fill="none" aria-hidden="true" focusable="false">
    <defs><path id={`login-contour-${variant}`} d="M-100 55C-20-50 65-45 132-12S272 73 318-16S465-92 466 6C467 94 363 148 289 126S123 47 80 118C27 205 170 237 207 318S159 475 51 504S-64 617-9 680" /></defs>
    {[0,1,2,3,4,5,6,7,8].map(i => <use key={i} href={`#login-contour-${variant}`} transform={`translate(${i*14} ${i*17}) scale(${1-i*.026})`} stroke={variant==='hero' && (i===1 || i===5) ? '#FF5F15' : 'currentColor'} strokeWidth="1" />)}
  </svg>;
}

function LoginBrand({ mobile = false }) {
  return <Link to="/" className={mobile ? "auth-mobile-logo" : "auth-brand-logo"} aria-label="FieldlinkNZ home">
    <img className="auth-logo-mark" src="/brand/fieldlink-mark.png" alt="" aria-hidden="true" />
    <span className="auth-wordmark">FIELDLINK<span className="auth-brand-orange">NZ</span><small>CIVIL CONSTRUCTION SOFTWARE</small></span>
  </Link>;
}

export default function Login() {
  const nav = useNavigate();
  const { login, setSession, beginVerification } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [bioBusy, setBioBusy] = useState(false);
  const [err, setErr] = useState("");
  const [maintenance, setMaintenance] = useState("");
  const showBiometric = canOfferBiometricUnlock() && localStorage.getItem("fl_passkey") === "1";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    setMaintenance("");
    try {
      const r = await login(email, password);
      if (r.verification) {
        setChallenge(r.verification); setPassword("");
      } else if (r.ok) {
        sessionStorage.setItem("fl_fresh_login", "1");
        toast.success(`Kia ora, ${r.user.name}`);
        nav("/app");
      } else if (r.maintenance) {
        setMaintenance(r.error);
      } else {
        setErr(r.error);
      }
    } catch {
      setErr("Unable to sign in. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const biometric = async () => {
    setBioBusy(true);
    setErr("");
    try {
      const data = await unlockWithPasskey();
      if (data.verification_required) {
        beginVerification(); setChallenge(data); setPassword("");
        return;
      }
      setSession(data);
      toast.success(`Kia ora, ${data.name}`);
      nav("/app");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "";
      if (!/cancel|abort|not allowed/i.test(String(msg))) {
        setErr(String(msg) || "Biometric unlock failed — use your password");
      }
    } finally {
      setBioBusy(false);
    }
  };

  if (challenge) return <LoginDeviceCheck challenge={challenge} onBack={() => setChallenge(null)} onVerified={data => {
    setSession(data);
    sessionStorage.setItem("fl_fresh_login", "1");
    toast.success("Identity verified");
    nav("/app");
  }} />;

  return (
    <main data-testid="login-page" className="auth-page">
      <aside className="auth-brand" aria-label="Fieldlink civil construction">
        <img src="/brand/login-coastal-earthworks.webp" alt="" aria-hidden="true" className="auth-brand-image" fetchPriority="high" />
        <div className="auth-brand-shade" aria-hidden="true" />
        <Contours variant="hero" />
        <LoginBrand />
        <div className="auth-brand-message">
          <h2>Build<br /><span className="auth-brand-orange">smarter</span><br />together.</h2>
          <p>One connected platform for civil construction<br className="auth-copy-break" /> businesses, from office to field.</p>
          <ul className="auth-features">
            {FEATURES.map(({ Icon, title, description }) => <li key={title}>
              <span className="auth-feature-icon"><Icon size={23} strokeWidth={2} aria-hidden="true" /></span>
              <div><h3>{title}</h3><p>{description}</p></div>
            </li>)}
          </ul>
        </div>
        <div className="auth-brand-footer"><p>Proudly built for<br />New Zealand's civil construction industry.</p></div>
      </aside>
      <section className="auth-form-area" aria-label="Sign in">
        <Contours variant="form" />
        <LoginBrand mobile />
        <div className="auth-card">
          <header className="auth-card-heading">
            <h1>Welcome back</h1>
            <p className="auth-secondary">Sign in to Fieldlink to continue.</p>
          </header>
          <form onSubmit={submit} aria-busy={busy} className="auth-form">
            {maintenance && <div role="alert" data-testid="login-maintenance" className="auth-error" style={{ borderColor: "#FF5F15", color: "#9a3b0c", background: "#fff4ee" }}>
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true"/>
              <span>{maintenance}</span>
            </div>}
            <div>
              <Label htmlFor="email" className="auth-label">Email address</Label>
              <div className="auth-field"><Mail className="auth-field-icon" size={18} aria-hidden="true" />
              <Input id="email" type="email" required autoComplete="username" inputMode="email"
                value={email} onChange={e=>setEmail(e.target.value)}
                aria-describedby={err ? "login-error" : undefined}
                className="auth-input" placeholder="you@company.co.nz" name="email" data-testid="login-email-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="auth-label">Password</Label>
              <div className="auth-field">
                <LockKeyhole className="auth-field-icon" size={18} aria-hidden="true" />
                <Input id="password" name="password" type={showPw ? "text" : "password"} required autoComplete="current-password"
                  value={password} onChange={e=>setPassword(e.target.value)}
                  aria-describedby={err ? "login-error" : undefined}
                  className="auth-input pr-12" data-testid="login-password-input" />
                <button type="button" onClick={()=>setShowPw(s=>!s)} aria-label={showPw ? "Hide password" : "Show password"}
                  aria-pressed={showPw} data-testid="login-password-toggle" className="auth-password-toggle">
                  {showPw ? <EyeOff className="w-[18px] h-[18px]"/> : <Eye className="w-[18px] h-[18px]"/>}
                </button>
              </div>
              <div className="auth-forgot">
                <Link to="/forgot-password" data-testid="login-forgot-link" className="auth-link inline-flex min-h-9 items-center text-sm">Forgot password?</Link>
              </div>
            </div>
            {err && <div id="login-error" role="alert" data-testid="login-error" className="auth-error">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true"/>
              <span>We couldn't sign you in. Check your email and password and try again. If you used device unlock, you can try your password instead.</span>
            </div>}
            <Button type="submit" disabled={busy} data-testid="login-submit-button" className="auth-submit w-full">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true"/>Signing in…</> : <>Sign in<ArrowRight size={17} aria-hidden="true" /></>}
            </Button>
            {showBiometric && <div className="auth-divider"><span/>Or continue with<span/></div>}
            {showBiometric && <Button type="button" variant="outline" disabled={bioBusy} onClick={biometric}
              data-testid="login-biometric-button" className="auth-secondary-button w-full whitespace-normal">
              <Fingerprint className="w-5 h-5"/>{bioBusy ? "Waiting for your device…" : "Unlock with fingerprint / Face ID"}
            </Button>}
          </form>
          <p className="auth-secondary auth-trial">New to Fieldlink? <Link to="/register" className="auth-link font-semibold" data-testid="login-register-link">Start a free trial</Link></p>
        </div>
          <footer className="auth-secondary auth-legal">
            <Link to="/terms" className="hover:underline" data-testid="login-terms-link">Terms of Service</Link>
            <span aria-hidden="true">·</span>
            <Link to="/privacy" className="hover:underline" data-testid="login-privacy-link">Privacy Policy</Link>
          </footer>
      </section>
    </main>
  );
}
